/*
 * UI metadata tracker.
 *
 * Historically this module just buffered every event in `metadataBuffer`
 * and let `Experiment.js` POST the whole array on `/end`.
 *
 * A8 (`EXPERIMENT_LAUNCH_CHECKLIST.md` §4, table rows 18 / 24 / 25)
 * extends it with live forwarding of *typed* client-side events to the
 * server via the new `POST /api/experiment/:exptID/client-event` endpoint:
 *
 *   - `error`                — `window.error` event listener.
 *   - `unhandled_rejection`  — `window.unhandledrejection` event listener.
 *   - `visibility_change`    — `document.visibilitychange` event listener.
 *   - `focus` / `blur`       — `window.focus` / `window.blur`.
 *   - `pageshow` / `pagehide` — bfcache restore detection.
 *
 * The legacy buffer is preserved unchanged so existing `/end` callers
 * keep working. The hooks are registered exactly once per page load
 * (idempotent re-init is a no-op).
 *
 * We deliberately use HTTP (rather than the per-block socket) so errors
 * during non-conversation blocks — questionnaire, stimuli, instruction —
 * are still captured. A future socket-aware path can layer on top via the
 * same `registerClientEventEmitter` API without changing this module.
 */

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

let metadataBuffer = [];

// A8 — live emitter scope. `Experiment.js` calls
// `registerClientEventEmitter({ exptID, userID, roomID, blockNo })` once
// the session metadata is known so the hooks below have somewhere to POST.
// The context object is shallow-merged so `updateClientEventContext({ blockNo })`
// keeps every event payload in sync with the current block.
let clientEventCtx = { exptID: null, userID: null, roomID: null, blockNo: null };
let listenersInstalled = false;

/**
 * Register the HTTP target + scope for live client-event forwarding.
 * Subsequent `error` / `visibility_change` / etc. captures will POST to
 * `${API_URL}/api/experiment/${exptID}/client-event` with the supplied
 * userID / roomID and current blockNo.
 *
 * @param {{ exptID?: string, userID?: string, roomID?: string, blockNo?: number|string|null }} ctx
 */
export function registerClientEventEmitter(ctx = {}) {
    clientEventCtx = {
        exptID: ctx.exptID || null,
        userID: ctx.userID || null,
        roomID: ctx.roomID || null,
        blockNo: ctx.blockNo == null ? null : ctx.blockNo,
    };
}

/**
 * Update the live emitter's context without re-registering. Cheap;
 * safe to call from a React effect.
 */
export function updateClientEventContext(patch = {}) {
    clientEventCtx = { ...clientEventCtx, ...patch };
}

/**
 * Drop the live emitter — call from a React cleanup effect so the
 * hooks below stop POSTing after unmount.
 */
export function unregisterClientEventEmitter() {
    clientEventCtx = { exptID: null, userID: null, roomID: null, blockNo: null };
}

/**
 * Internal: forward a typed event to the server. Best-effort; swallows
 * errors so a throw inside a browser handler never propagates back into
 * the browser's default error reporting.
 */
function _emitClientEvent(kind, payload) {
    const { exptID, userID, roomID } = clientEventCtx;
    if (!exptID || !userID || !roomID) return; // not registered yet
    try {
        const body = JSON.stringify({
            userID,
            roomID,
            kind,
            payload: payload || null,
            blockNo: clientEventCtx.blockNo == null ? null : clientEventCtx.blockNo,
            occurred_at: new Date().toISOString(),
        });
        // `fetch` keeps the dependency footprint of this module to zero.
        // `keepalive: true` lets the request survive page unload (the
        // pagehide / beforeunload handlers depend on this).
        fetch(`${API_URL}/api/experiment/${encodeURIComponent(exptID)}/client-event`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
        }).catch(() => { /* never throw */ });
    } catch (_e) { /* never throw */ }
}

// Utility to add metadata event with a timestamp (legacy buffer path).
export function captureEvent(eventType, data = {}) {
    const eventData = {
        type: eventType,
        timestamp: new Date().toISOString(),
        ...data,
    };
    metadataBuffer.push(eventData);
}

// Setup common event listeners. Idempotent: a second call is a no-op so
// React StrictMode's double-invocation doesn't double-register.
export function initEventTracking() {
    if (listenersInstalled) return;
    listenersInstalled = true;

    window.addEventListener('resize', () => {
        captureEvent('resize', { width: window.innerWidth, height: window.innerHeight });
    });

    window.addEventListener('beforeunload', () => {
        captureEvent('beforeunload');
    });

    document.addEventListener('mousemove', (event) => {
        captureEvent('mousemove', { x: event.clientX, y: event.clientY });
    });

    document.addEventListener('click', (event) => {
        captureEvent('click', { x: event.clientX, y: event.clientY });
    });

    document.addEventListener('keydown', (event) => {
        captureEvent('keydown', { key: event.key });
    });

    // ------------------------------------------------------------------
    // A8 — error / focus / visibility hooks. Each both pushes to the
    // legacy buffer (so `/end` keeps its whole-buffer dump) AND emits
    // the typed `expt_client_event` so the server can persist it live.
    // ------------------------------------------------------------------
    window.addEventListener('error', (event) => {
        const payload = {
            message: event?.message || String(event?.error || ''),
            filename: event?.filename || null,
            lineno: typeof event?.lineno === 'number' ? event.lineno : null,
            colno: typeof event?.colno === 'number' ? event.colno : null,
            stack: event?.error?.stack || null,
        };
        captureEvent('error', payload);
        _emitClientEvent('error', payload);
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event?.reason;
        const payload = {
            message: reason && typeof reason === 'object'
                ? (reason.message || String(reason))
                : String(reason == null ? '' : reason),
            stack: reason && typeof reason === 'object' && typeof reason.stack === 'string'
                ? reason.stack
                : null,
        };
        captureEvent('unhandledrejection', payload);
        _emitClientEvent('unhandled_rejection', payload);
    });

    document.addEventListener('visibilitychange', () => {
        const payload = { visibilityState: document.visibilityState };
        captureEvent('visibilitychange', payload);
        _emitClientEvent('visibility_change', payload);
    });

    window.addEventListener('focus', () => {
        captureEvent('focus');
        _emitClientEvent('focus', null);
    });

    window.addEventListener('blur', () => {
        captureEvent('blur');
        _emitClientEvent('blur', null);
    });

    window.addEventListener('pageshow', (event) => {
        const payload = { persisted: !!event?.persisted };
        captureEvent('pageshow', payload);
        _emitClientEvent('pageshow', payload);
    });

    window.addEventListener('pagehide', (event) => {
        const payload = { persisted: !!event?.persisted };
        captureEvent('pagehide', payload);
        _emitClientEvent('pagehide', payload);
    });
}

// Function to retrieve current metadata.
export function getMetadata() {
    return [...metadataBuffer];
}

// Clear metadata buffer after sending.
export function clearMetadata() {
    metadataBuffer = [];
}

// Exported only for testability — lets unit tests inspect the singleton
// state without exporting `metadataBuffer` itself.
export const __test = {
    _getBufferLength: () => metadataBuffer.length,
    _isListenerInstalled: () => listenersInstalled,
    _reset: () => {
        metadataBuffer = [];
        clientEventCtx = { exptID: null, userID: null, roomID: null, blockNo: null };
        listenersInstalled = false;
    },
};